import type { PlanCell } from '@features/workshop-plan/types';

import { cn } from '@shared/utils/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip';

import { getShortName, getCellStatus } from '@features/workshop-plan/utils/plan-cell';
import { TONE_ICONS, TONE_STYLES } from '@features/workshop-plan/components/plan-tone';

type Props = {
  cell: PlanCell;
  /** Stretches the tile over its band (shelf) instead of giving it a minimum height (floor). */
  minHeight?: number;
  dashed?: boolean;
  /** Shows the whole location name instead of what follows the last dash (large floor cells have room for it). */
  fullName?: boolean;
  /** Pushed to the background because it is not what the operator is looking for. */
  dimmed?: boolean;
};

/**
 * One location of the plan: its name, then the reference of the order it holds, or "Vide".
 * Boxes leave the workshop from the "Sortie caisses" scan screen, not from the plan.
 */
export const PlanCellTile = ({ cell, minHeight = 72, dashed = false, fullName = false, dimmed = false }: Props) => {
  const { occupied, tone, summary, description, details } = getCellStatus(cell);
  const Icon = TONE_ICONS[tone];
  const style = TONE_STYLES[tone];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          aria-label={description}
          className={cn(
            'flex min-w-0 flex-col items-center justify-center gap-px rounded-lg border p-1 text-center transition-[opacity,background-color] duration-150 outline-none hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none',
            dashed ? 'border-2' : 'border',
            occupied ? 'border-solid border-l-4 text-foreground' : 'border-dashed text-muted-foreground',
            style.border,
            occupied && style.band,
            style.bg,
            style.bgHover,
            'cursor-default',
            dimmed ? 'opacity-28' : 'opacity-100'
          )}
          role="group"
          style={{ minHeight }}
          tabIndex={0}
        >
          <span className="font-mono text-xs leading-[1.15] font-bold [overflow-wrap:anywhere]">
            {fullName ? cell.name : getShortName(cell.name)}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 text-xs leading-[1.15] [overflow-wrap:anywhere]',
              occupied ? 'font-mono font-semibold' : 'font-normal'
            )}
          >
            {occupied && <Icon aria-hidden className="size-3" />}
            {summary}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col">
          {details.map((line, index) => (
            <span className={index === 0 ? 'font-bold' : undefined} key={line}>
              {line}
            </span>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
